import React, { Suspense, ReactNode } from "react";
import { useIsFeatureFlagActive } from "./api";
import { FeatureFlagContext } from "./types";

interface FeatureFlagProps {
  /** Name of the feature flag to check */
  name: string;
  /** Context for feature flag evaluation (optional for global flags) */
  context?: FeatureFlagContext;
  /** Child components to render if the feature flag is active */
  children: ReactNode;
  /** Optional fallback content when feature flag is not active */
  fallback?: ReactNode;
}

/**
 * Component that performs the feature flag check
 */
const FeatureFlagChecker: React.FC<FeatureFlagProps> = ({
  name,
  context = {},
  children,
  fallback = null
}) => {
  const isActive = useIsFeatureFlagActive(name, context);
  return isActive ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that conditionally renders children based on a feature flag
 * 
 * @example
 * ```tsx
 * // Simple usage
 * <FeatureFlag 
 *   name="SHOW_CHARGE_ESTIMATE" 
 *   context={{ installationId: 123 }}
 * >
 *   <ChargeEstimateComponent />
 * </FeatureFlag>
 * 
 * // With fallback content
 * <FeatureFlag
 *   name="NEW_UI"
 *   context={{ organizationId: organization.id }}
 *   fallback={<LegacyComponent />}
 * >
 *   <NewComponent />
 * </FeatureFlag>
 * ```
 */
const FeatureFlag: React.FC<FeatureFlagProps> = (props) => {
  return (
    <Suspense fallback={<span />}>
      <FeatureFlagChecker {...props} />
    </Suspense>
  );
};

export default FeatureFlag;